// PSD reader — section walker.
//
// Scope of this file (M6 bootstrap):
//   - Validate the 8BPS signature and version.
//   - Parse the 5-section layout: header, color mode, image resources,
//     layer & mask info, and the *offset* of the merged-image data.
//   - Inside section 4, parse the layer records' metadata (rect, channels,
//     blend mode, name, mask sub-block as raw bytes) and collect each
//     layer's additional-info blocks as raw bytes.
//
// Explicit non-goals for this agent:
//   - No decompression (RLE / ZIP / ZIP-prediction).
//   - No interpretation of channel image bytes.
//   - No semantic parsing of additional-info blocks (luni / lsct / lfx2 / …).
//
// PSB (large document, version 2) note: several length fields widen from
// 32-bit to 64-bit.  We thread a `largeDoc` flag through the parser so that
// adding PSB later is a contained change.  Right now the entry point only
// accepts version 1 to keep the surface small for the bootstrap.

import { BinaryReader, BinaryReaderError } from "./BinaryReader";
import type {
  PsdAdditionalLayerInfo,
  PsdChannelInfo,
  PsdDepth,
  PsdFile,
  PsdHeader,
  PsdLayerInfo,
  PsdLayerRecord,
  PsdResource,
  PsdVersion,
} from "./types";

export class PsdParseError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "PsdParseError";
    if (cause !== undefined) {
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

const SIGNATURE = "8BPS";
const RESOURCE_SIGNATURE = "8BIM";
const ADDITIONAL_SIG_8BIM = "8BIM";
const ADDITIONAL_SIG_8B64 = "8B64";

/**
 * Keys in the Additional Layer Information section whose length field is
 * 64-bit in PSB (large-document, version 2) files.  Per the Adobe spec these
 * are: `LMsk Lr16 Lr32 Layr Mt16 Mt32 Mtrn Alph FMsk lnk2 FEid FXid PxSD`.
 *
 * IMPORTANT: In PSD v1 (non-PSB) files these keys use a standard 32-bit
 * length field, just like every other `8BIM` block.  Only when the document
 * version is 2 (PSB) do they switch to 64-bit.  (The `8B64` signature is an
 * independent signal — it always implies a 64-bit length regardless of
 * document version.)
 */
const ADDITIONAL_KEYS_64BIT = new Set([
  "LMsk",
  "Lr16",
  "Lr32",
  "Layr",
  "Mt16",
  "Mt32",
  "Mtrn",
  "Alph",
  "FMsk",
  "lnk2",
  "FEid",
  "FXid",
  "PxSD",
]);

function isDepth(n: number): n is PsdDepth {
  return n === 1 || n === 8 || n === 16 || n === 32;
}

function isVersion(n: number): n is PsdVersion {
  return n === 1 || n === 2;
}

/** Round `n` up to the next multiple of `pad`. */
function padTo(n: number, pad: number): number {
  const r = n % pad;
  return r === 0 ? n : n + (pad - r);
}

/**
 * Entry point.  Parses the structural skeleton of a PSD file and returns a
 * fully-typed {@link PsdFile} record.  Channel image data is *not* decoded;
 * each layer carries the byte offset where its channel data begins.
 */
export function parsePsd(bytes: Uint8Array): PsdFile {
  const r = new BinaryReader(bytes);
  try {
    const header = parseHeader(r);
    const largeDoc = header.version === 2;
    const colorModeData = parseColorModeData(r, largeDoc);
    const imageResources = parseImageResources(r, largeDoc);
    const layerInfo = parseLayerAndMaskInfo(r, largeDoc, header.depth);
    const imageDataOffset = r.tell();
    return {
      header,
      colorModeData,
      imageResources,
      layerInfo,
      imageDataOffset,
    };
  } catch (err) {
    if (err instanceof PsdParseError) throw err;
    if (err instanceof BinaryReaderError) {
      throw new PsdParseError(`PSD parse failed: ${err.message}`, err);
    }
    throw err;
  }
}

// -- Section 1: File header -----------------------------------------------

function parseHeader(r: BinaryReader): PsdHeader {
  const sig = r.readAscii(4);
  if (sig !== SIGNATURE) {
    throw new PsdParseError(
      `not a PSD file: expected signature "${SIGNATURE}", got "${sig}"`,
    );
  }
  const version = r.readU16();
  if (!isVersion(version)) {
    throw new PsdParseError(`unsupported PSD version ${version}`);
  }
  // Reserved 6 bytes — must be zero per spec, but real-world files sometimes
  // contain garbage.  Skip without validating.
  r.skip(6);
  const channels = r.readU16();
  if (channels < 1 || channels > 56) {
    throw new PsdParseError(
      `invalid channel count ${channels} (must be 1..56)`,
    );
  }
  const height = r.readU32();
  const width = r.readU32();
  const maxDim = version === 2 ? 300_000 : 30_000;
  if (height < 1 || height > maxDim) {
    throw new PsdParseError(
      `invalid height ${height} (must be 1..${maxDim} for v${version})`,
    );
  }
  if (width < 1 || width > maxDim) {
    throw new PsdParseError(
      `invalid width ${width} (must be 1..${maxDim} for v${version})`,
    );
  }
  // Guard: total pixel count.  300 M pixel limit matches issue #175 acceptance.
  const MAX_PIXEL_COUNT = 300_000_000;
  if (width * height > MAX_PIXEL_COUNT) {
    throw new PsdParseError(
      `document too large: ${width} × ${height} = ${width * height} pixels exceeds ` +
        `the ${MAX_PIXEL_COUNT.toLocaleString()} pixel limit`,
    );
  }

  const depth = r.readU16();
  if (!isDepth(depth)) {
    throw new PsdParseError(
      `invalid bit depth ${depth} (must be 1/8/16/32)`,
    );
  }
  const colorMode = r.readU16();
  return { version, channels, height, width, depth, colorMode };
}

// -- Section 2: Color mode data -------------------------------------------

function parseColorModeData(r: BinaryReader, largeDoc: boolean): Uint8Array {
  const length = largeDoc ? r.readU64() : r.readU32();
  return r.readBytes(length);
}

// -- Section 3: Image resources -------------------------------------------

function parseImageResources(r: BinaryReader, largeDoc: boolean): PsdResource[] {
  const sectionLength = largeDoc ? r.readU64() : r.readU32();
  const end = r.tell() + sectionLength;
  const out: PsdResource[] = [];
  while (r.tell() < end) {
    const sig = r.readAscii(4);
    if (sig !== RESOURCE_SIGNATURE) {
      throw new PsdParseError(
        `bad image resource signature "${sig}" at offset ${r.tell() - 4}`,
      );
    }
    const id = r.readU16();
    const name = r.readPascalString(2);
    const dataLength = r.readU32();
    const data = r.readBytes(dataLength);
    if (dataLength % 2 === 1) r.skip(1); // pad to even
    out.push({ id, name, data });
  }
  if (r.tell() !== end) {
    throw new PsdParseError(
      `image resources section over-read: expected end at ${end}, ` +
        `cursor at ${r.tell()}`,
    );
  }
  return out;
}

// -- Section 4: Layer & mask information ----------------------------------

function parseLayerAndMaskInfo(
  r: BinaryReader,
  largeDoc: boolean,
  depth: PsdDepth,
): PsdLayerInfo {
  const sectionLength = largeDoc ? r.readU64() : r.readU32();
  const sectionEnd = r.tell() + sectionLength;

  // Empty section is legal (a file with only a flat composite).
  if (sectionLength === 0) {
    return {
      layerCount: 0,
      layerCountRaw: 0,
      layers: [],
      globalLayerMaskData: new Uint8Array(0),
      additionalInfo: [],
    };
  }

  // Layer info sub-section.
  const layerInfoLength = largeDoc ? r.readU64() : r.readU32();
  const layerInfoEnd = r.tell() + layerInfoLength;

  // Guard: layerInfoEnd must not exceed sectionEnd.  Some malformed PSDs
  // declare a layerInfoLength that includes channel pixel data which spills
  // past the section boundary.  Clamp to sectionEnd so that
  // advancePastChannelImageData cannot walk into the merged-image section.
  const clampedLayerInfoEnd = Math.min(layerInfoEnd, sectionEnd);
  if (layerInfoEnd > sectionEnd) {
    console.warn(
      `parsePsd: layerInfoLength (${layerInfoLength}) extends past layer&mask ` +
        `section boundary (sectionEnd=${sectionEnd}, layerInfoEnd=${layerInfoEnd}); ` +
        `clamping to sectionEnd`,
    );
  }

  // Allocation caps (#175).
  const MAX_LAYER_COUNT = 8192;
  const MAX_CUMULATIVE_CHANNEL_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

  let layerCountRaw = 0;
  let layers: PsdLayerRecord[] = [];
  if (layerInfoLength > 0) {
    layerCountRaw = r.readI16();
    const layerCount = Math.abs(layerCountRaw);

    // Guard: layer count sanity.
    if (layerCount > MAX_LAYER_COUNT) {
      throw new PsdParseError(
        `file claims ${layerCount} layers but the maximum supported is ${MAX_LAYER_COUNT} — ` +
          `the file is likely corrupt or malicious`,
      );
    }

    // Parse layer records; each record reads its own extras and advances the
    // cursor.  Track per-layer cursor positions for diagnostics.
    const layerRecordStartCursors: number[] = [];
    for (let i = 0; i < layerCount; i++) {
      layerRecordStartCursors.push(r.tell());
      layers.push(parseLayerRecord(r, largeDoc));
    }

    // Guard: cumulative channel data.
    let cumulativeChannelBytes = 0;
    for (const layer of layers) {
      for (const ch of layer.channels) {
        cumulativeChannelBytes += ch.length;
        if (cumulativeChannelBytes > MAX_CUMULATIVE_CHANNEL_BYTES) {
          throw new PsdParseError(
            `file claims more than ${MAX_CUMULATIVE_CHANNEL_BYTES / (1024 * 1024 * 1024)} GB ` +
              `of channel data — the file is likely corrupt or malicious`,
          );
        }
      }
    }
    const bufRemaining = r.remaining();
    if (cumulativeChannelBytes > bufRemaining) {
      throw new PsdParseError(
        `file claims ${cumulativeChannelBytes} bytes of channel data but only ` +
          `${bufRemaining} bytes remain in the buffer — ` +
          `the file is truncated or corrupt`,
      );
    }

    // Channel image data follows immediately after the layer records.  We do
    // *not* decode it here — instead we walk past it using the per-channel
    // lengths recorded in each layer.  This is what lets the next agent
    // jump straight to a layer's channel bytes via `channelImageOffset`.
    advancePastChannelImageData(r, layers, clampedLayerInfoEnd, depth, sectionEnd);

    // Layer info sub-section is padded to a multiple of 2.
    // Always realign to clampedLayerInfoEnd regardless of direction —
    // over-reading (cursor > clampedLayerInfoEnd) is recoverable here.
    if (r.tell() !== clampedLayerInfoEnd) {
      if (r.tell() > clampedLayerInfoEnd) {
        console.warn(
          `parsePsd: layer info over-read detected; cursor=${r.tell()}, ` +
            `layerInfoEnd=${layerInfoEnd}, sectionEnd=${sectionEnd}. ` +
            `Recovering by seeking to sectionEnd. ` +
            `Layer count=${layerCount}. ` +
            `Layer record start offsets: [${layerRecordStartCursors.join(", ")}]. ` +
            `Per-layer channel totals: [${layers.map((l) => l.channels.reduce((s, c) => s + c.length, 0)).join(", ")}].`,
        );
        // Graceful recovery: seek to the section end.  Layers parsed so far
        // are kept; channel offsets may be incorrect but at least the file
        // opens and the user sees their layers.
        r.seek(sectionEnd);
        return {
          layerCount: Math.abs(layerCountRaw),
          layerCountRaw,
          layers,
          globalLayerMaskData: new Uint8Array(0),
          additionalInfo: [],
        };
      }
      r.skip(clampedLayerInfoEnd - r.tell());
    }
  }

  // Global Layer Mask Info sub-section.
  let globalLayerMaskData: Uint8Array = new Uint8Array(0);
  if (r.tell() < sectionEnd) {
    const globalLen = r.readU32();
    // Guard against a malformed globalLen that would overflow sectionEnd.
    const safeGlobalLen = Math.min(globalLen, sectionEnd - r.tell());
    if (safeGlobalLen !== globalLen) {
      console.warn(
        `parsePsd: global layer mask length ${globalLen} overruns section ` +
          `(only ${sectionEnd - r.tell()} bytes remain); truncating`,
      );
    }
    globalLayerMaskData = r.readBytes(safeGlobalLen);
  }

  // Trailing Additional Layer Information blocks (document-level).
  // Per the Adobe spec these are padded to 4-byte boundaries (unlike per-layer
  // blocks which use 2-byte padding).
  const additionalInfo: PsdAdditionalLayerInfo[] = [];
  while (r.tell() + 12 <= sectionEnd) {
    const before = r.tell();
    const block = parseAdditionalLayerInfo(r, largeDoc, 4, true);
    additionalInfo.push(block);
    // Safety: if parseAdditionalLayerInfo did not advance the cursor (e.g. a
    // zero-length unknown block with no skip), break to avoid an infinite loop.
    if (r.tell() === before) break;
  }

  // Section may have trailing pad bytes; just skip to the declared end.
  if (r.tell() < sectionEnd) {
    r.skip(sectionEnd - r.tell());
  } else if (r.tell() > sectionEnd) {
    // Graceful recovery: log diagnostics and seek to the declared end.
    console.warn(
      `parsePsd: layer & mask info over-read: expected end at ${sectionEnd}, ` +
        `cursor at ${r.tell()} (delta +${r.tell() - sectionEnd} bytes). ` +
        `Recovering by seeking to declared section end. ` +
        `Layer count=${layers.length}, layerInfoLength=${layerInfoLength}, ` +
        `sectionLength=${sectionLength}. ` +
        `Per-layer channel totals: [${layers.map((l) => l.channels.reduce((s, c) => s + c.length, 0)).join(", ")}].`,
    );
    r.seek(sectionEnd);
  }

  return {
    layerCount: Math.abs(layerCountRaw),
    layerCountRaw,
    layers,
    globalLayerMaskData,
    additionalInfo,
  };
}

function parseLayerRecord(
  r: BinaryReader,
  largeDoc: boolean,
): PsdLayerRecord {
  const top = r.readI32();
  const left = r.readI32();
  const bottom = r.readI32();
  const right = r.readI32();
  const channelCount = r.readU16();
  if (channelCount > 56) {
    throw new PsdParseError(
      `layer channel count ${channelCount} exceeds max 56`,
    );
  }
  const channels: PsdChannelInfo[] = [];
  for (let c = 0; c < channelCount; c++) {
    const id = r.readI16();
    const length = largeDoc ? r.readU64() : r.readU32();
    channels.push({ id, length });
  }

  const blendSig = r.readAscii(4);
  if (blendSig !== "8BIM") {
    throw new PsdParseError(
      `bad blend-mode signature "${blendSig}" in layer record`,
    );
  }
  const blendModeKey = r.readAscii(4);
  const opacity = r.readU8();
  const clipping = r.readU8();
  const flags = r.readU8();
  r.skip(1); // filler

  const extraDataLength = r.readU32();
  const extraDataEnd = r.tell() + extraDataLength;

  // -- Mask data ----------------------------------------------------
  const maskDataLength = r.readU32();
  const maskData = r.readBytes(maskDataLength);

  // -- Layer blending ranges ---------------------------------------
  const blendingRangesLength = r.readU32();
  const blendingRangesData = r.readBytes(blendingRangesLength);

  // -- Legacy Pascal-string name (pad to 4) ------------------------
  const name = r.readPascalString(4);

  // -- Additional Layer Information blocks -------------------------
  const additionalInfo: PsdAdditionalLayerInfo[] = [];
  while (r.tell() < extraDataEnd) {
    additionalInfo.push(parseAdditionalLayerInfo(r, largeDoc));
  }
  if (r.tell() !== extraDataEnd) {
    // Some files over-pad the extra data section; just realign.
    r.seek(extraDataEnd);
  }

  return {
    top,
    left,
    bottom,
    right,
    channelCount,
    channels,
    blendModeKey,
    opacity,
    clipping,
    flags,
    name,
    maskData,
    blendingRangesData,
    channelImageOffset: -1, // filled in after all records by caller
    additionalInfo,
  };
}

/**
 * Walk past the per-layer channel image data, recording the start offset on
 * each layer.  We don't decode anything — just sum lengths.
 *
 * `layerInfoEnd` is the declared end of the layer-info sub-section (already
 * clamped to `sectionEnd` by the caller).  `sectionEnd` is the hard outer
 * boundary of the layer-and-mask info section.
 *
 * When channel data would overflow the section boundary, we log a warning and
 * stop advancing — the over-read is detected by the caller and triggers
 * graceful recovery.
 */
function advancePastChannelImageData(
  r: BinaryReader,
  layers: PsdLayerRecord[],
  layerInfoEnd: number,
  _depth: PsdDepth,
  sectionEnd: number,
): void {
  const hardEnd = Math.min(layerInfoEnd, sectionEnd);
  for (const layer of layers) {
    layer.channelImageOffset = r.tell();
    let total = 0;
    for (const ch of layer.channels) total += ch.length;
    if (r.tell() + total > hardEnd) {
      // The declared channel data extends past the section boundary.  Log a
      // warning with enough context for the next investigation iteration, then
      // stop — the caller's over-read check will handle recovery.
      console.warn(
        `parsePsd: channel data for layer "${layer.name}" overruns section ` +
          `boundary (declared total=${total} bytes, ` +
          `${hardEnd - r.tell()} bytes remain to section end). ` +
          `Channels: [${layer.channels.map((c) => `id=${c.id} len=${c.length}`).join(", ")}]. ` +
          `Stopping channel-data advance early; caller will recover.`,
      );
      // Seek to the hard end so the caller detects over-read correctly.
      r.seek(hardEnd);
      return;
    }
    r.skip(total);
  }
}

/**
 * Parse a single Additional Layer Information block.
 *
 * @param blockPad - Alignment of the data payload:
 *   - **2** for per-layer additional-info blocks (the common case; Photoshop
 *     pads these to a 2-byte boundary in practice).
 *   - **4** for document-level (section-level) additional-info blocks.  The
 *     Adobe PSD spec states the trailing additional-layer-info section pads
 *     each block's data to a 4-byte boundary (§ "Additional Layer Information").
 *     Using 2 here causes a 2-byte misalignment on every odd-length block,
 *     which accumulates and manifests as the "  8B" signature garbage read
 *     (issue #163).
 *
 * @param sectionLevel - When **true**, the block is in the document-level
 *   trailing Additional Layer Information section.  In that context, even
 *   keys in `ADDITIONAL_KEYS_64BIT` (e.g. `lnk2`, `FMsk`) use a 32-bit
 *   length field when the signature is `8BIM`; only `8B64`-signed blocks use
 *   64-bit length.  Per-layer additional-info blocks (sectionLevel=false) DO
 *   use 64-bit length for those keys even with an `8BIM` signature — that is
 *   what issue #156 fixed.
 */
function parseAdditionalLayerInfo(
  r: BinaryReader,
  largeDoc: boolean,
  blockPad: 2 | 4 = 2,
  sectionLevel = false,
): PsdAdditionalLayerInfo {
  const sigOffset = r.tell();
  const signature = r.readAscii(4);
  if (signature !== ADDITIONAL_SIG_8BIM && signature !== ADDITIONAL_SIG_8B64) {
    // Real Photoshop PSDs occasionally contain proprietary-extension blocks
    // whose signature is neither "8BIM" nor "8B64" (e.g. blocks from third-
    // party plug-ins, or newer PS features not yet in the public spec).
    // Mainstream readers (Photopea, psd.js, psd-tools) skip these rather than
    // throwing, using the key(4) + length(4) + padded-data layout that all
    // additional-info blocks share.  We do the same: log a warning and skip
    // the block so the rest of the layer/document parses cleanly.
    // Known non-standard signatures seen in the wild: "PHUT" (Photoshop
    // internal UI state), "ACTM" (action manager metadata), and malformed
    // reads of "8BIM" when a preceding block's length was slightly off.
    const key = r.readAscii(4);
    const length = r.readU32(); // unknown-sig blocks always use 32-bit length
    const padded = padTo(length, blockPad);
    console.warn(
      `Unknown additional-info signature "${signature}" (key "${key}") at offset ${sigOffset}, skipping ${length} bytes`,
    );
    // Try to skip the declared length.  If the declared length is sane, this
    // moves the cursor to the start of the next block.  If it would overflow,
    // advance to the end of the buffer so the caller's loop exits cleanly.
    const afterSkip = r.tell() + padded;
    if (afterSkip > r.tell() + r.remaining()) {
      r.skip(r.remaining()); // consume rest; caller loop exits
      return { signature, key, data: new Uint8Array(0) };
    }
    {
      r.skip(padded);
      // Belt-and-suspenders: verify the next 4 bytes look like a known magic.
      // If not, scan forward (up to 1 KB) for the next valid magic so that a
      // single corrupt/misaligned block does not cascade into total parse
      // failure.
      const RESYNC_SCAN_LIMIT = 1024;
      const scanEnd = Math.min(r.tell() + RESYNC_SCAN_LIMIT, r.tell() + r.remaining());
      let resynced = false;
      const savedPos = r.tell();
      while (r.tell() + 4 <= scanEnd) {
        const pos = r.tell();
        // Peek 4 bytes without a method that throws: use readAscii then seek back.
        const peek = r.readAscii(4);
        if (peek === ADDITIONAL_SIG_8BIM || peek === ADDITIONAL_SIG_8B64) {
          // We found a valid magic.  Seek back so the caller re-reads the sig.
          r.seek(pos);
          if (pos !== savedPos) {
            console.warn(
              `Resynced additional-info parser at offset ${pos} after misaligned block "${signature}"/"${key}"`,
            );
          }
          resynced = true;
          break;
        }
        // Step back 3 bytes — the first byte of the magic may overlap.
        r.seek(pos + 1);
      }
      if (!resynced) {
        // No valid magic found within scan range; reset to just after the skip
        // and let the caller's loop boundary (sectionEnd / extraDataEnd) stop
        // the walk naturally.
        r.seek(afterSkip);
      }
    }
    // Return a placeholder so callers can see the block was present.
    return { signature, key, data: new Uint8Array(0) };
  }
  const key = r.readAscii(4);
  // Length is 64-bit when:
  //   (a) The signature is "8B64".
  //   (b) The signature is "8BIM", the key is in ADDITIONAL_KEYS_64BIT, AND the
  //       block is per-layer (not section-level).  In the document-level trailing
  //       Additional Layer Information section, even these keys use 32-bit length
  //       with 8BIM — only 8B64-signed blocks get 64-bit treatment there.
  //       (Issue #156 added the per-layer case; issue #163 restricts it to
  //       non-section-level contexts.)
  const wide =
    signature === ADDITIONAL_SIG_8B64 ||
    (!sectionLevel && ADDITIONAL_KEYS_64BIT.has(key));
  const length = wide ? r.readU64() : r.readU32();
  const data = r.readBytes(length);
  // Per-layer blocks are padded to 2 bytes; document-level blocks to 4 bytes.
  // The blockPad parameter controls which applies — callers must pass the right
  // value.  See the function JSDoc for rationale.
  const padded = padTo(length, blockPad);
  if (padded !== length) r.skip(padded - length);
  return { signature, key, data };
}
